import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Phone, ChevronDown, ArrowRight, Smartphone, Wifi, ArrowUpCircle, Signal, Headphones, LifeBuoy } from "lucide-react";
import { api } from "../lib/api";
import { BUSINESS } from "../data/site";
import { Link } from "react-router-dom";

const ICONS = { Smartphone, Wifi, ArrowUpCircle, Signal, Headphones, LifeBuoy };

export default function Services() {
    const [services, setServices] = useState([]);
    const [openIdx, setOpenIdx] = useState(0);

    useEffect(() => {
        api.get("/services").then((r) => setServices(r.data));
    }, []);

    return (
        <>
            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="hero-glow" />
                <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28">
                    <div className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold">Services</div>
                    <h1 className="mt-3 font-display font-extrabold text-slate-900 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl">
                        Everything wireless, handled by people who actually care.
                    </h1>
                    <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
                        From device sales to activations, upgrades and tech support — we make it fast, friendly, and honest.
                    </p>
                </div>
            </section>

            {/* Services list */}
            <section data-testid="services-list" className="max-w-7xl mx-auto px-5 lg:px-10 pb-20 space-y-5">
                {services.map((s, i) => {
                    const Icon = ICONS[s.icon] || Smartphone;
                    const open = openIdx === i;
                    return (
                        <motion.div
                            key={s.id}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4 }}
                            data-testid={`service-row-${i}`}
                            className="bg-white rounded-3xl border border-slate-100 premium-shadow overflow-hidden"
                        >
                            <button
                                onClick={() => setOpenIdx(open ? -1 : i)}
                                className="w-full flex items-center gap-5 p-6 text-left hover:bg-slate-50/50 transition"
                                data-testid={`service-toggle-${i}`}
                            >
                                <div className="h-14 w-14 rounded-2xl bg-slate-900 text-white grid place-items-center flex-shrink-0">
                                    <Icon className="w-6 h-6" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h2 className="font-display font-bold text-slate-900 text-xl">{s.name}</h2>
                                    <p className="text-slate-600 text-sm mt-1">{s.short_description}</p>
                                </div>
                                <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                            </button>
                            {open && (
                                <div className="px-6 pb-6 pt-1 grid lg:grid-cols-3 gap-6 border-t border-slate-100">
                                    <div className="lg:col-span-2">
                                        <p className="text-slate-600 leading-relaxed">{s.long_description}</p>
                                        <ul className="mt-5 grid sm:grid-cols-2 gap-2.5">
                                            {s.benefits?.map((b) => (
                                                <li key={b} className="flex items-start gap-2.5 text-sm text-slate-700">
                                                    <div className="h-5 w-5 rounded-full bg-emerald-50 text-emerald-600 grid place-items-center flex-shrink-0 mt-0.5">
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                                    </div>
                                                    {b}
                                                </li>
                                            ))}
                                        </ul>
                                        {s.faqs?.length > 0 && (
                                            <div className="mt-6 space-y-3">
                                                {s.faqs.map((f, j) => (
                                                    <div key={j} className="rounded-2xl bg-slate-50 p-4">
                                                        <div className="font-semibold text-slate-900 text-sm">{f.q}</div>
                                                        <div className="text-slate-600 text-sm mt-1">{f.a}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="bg-slate-900 rounded-2xl p-6 text-white flex flex-col">
                                        <div className="text-[10px] uppercase tracking-[0.22em] text-cyan-400 font-semibold">Ready to start?</div>
                                        <div className="mt-2 font-display font-bold text-xl">Get help with {s.name.toLowerCase()}.</div>
                                        <p className="text-white/70 text-sm mt-2">Walk in or call — most services done same day.</p>
                                        <div className="mt-auto pt-5 flex flex-col gap-2">
                                            <a href={`tel:${BUSINESS.phoneRaw}`} className="inline-flex items-center justify-center gap-2 h-10 rounded-full bg-white text-slate-900 font-semibold hover:bg-slate-100 transition">
                                                <Phone className="w-4 h-4" /> Call
                                            </a>
                                            <Link to="/contact" className="inline-flex items-center justify-center gap-2 h-10 rounded-full bg-white/10 text-white border border-white/20 font-semibold hover:bg-white/20 transition">
                                                Send Inquiry <ArrowRight className="w-4 h-4" />
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    );
                })}
            </section>
        </>
    );
}
